"use client";

/**
 * Requirements Summary:
 * - Format and validate Team Join Codes inline for parent-facing forms.
 * - Surface validated team/org/location/fee details to callers so checkout
 *   add-ons like Team registration coupons can use the same validated context.
 * - Preserve required team auto-pay context and ignore stale validation results
 *   as parents edit codes.
 */

import React, { useState, useCallback, useRef } from 'react';
import TextField from './TextField';
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';
import Typography from '@/components/ui/Typography';
import { createLogger } from '@/utils/logger/devLogger';
import { resolveJoinDenialMessage } from '@/utils/teams/joinCodeDenials';

const logger = createLogger('JoinCodeField');

interface JoinCodeInfo {
  organizationId: string;
  /**
   * #3362: the team this code resolves to. The validator has always returned it
   * (`teamId: data.id`); this component simply dropped it. The join-by-code
   * checkout needs it to disclose the balance auto-pay will collect, because a
   * checkout authorization covers the whole season with no future-invoices-only
   * carve-out.
   */
  teamId: string;
  locationId?: string | null;
  teamName: string;
  organizationName: string;
  locationName?: string;
  enrollmentFee?: number;
  registrationFeeWaivedForCrossover?: boolean;
  requiresSeasonAutoPay?: boolean;
  feeSchedule?: any[] | null;
  seasonStartDate?: string | null;
  seasonEndDate?: string | null;
  // U5a (semi-monthly auto-invoicing): expose the gym's auto-invoice toggles
  // so the JoinTeamModal can render a parent-facing banner when monthly
  // tuition is split into two invoices per month.
  organizationAutoInvoiceEnabled?: boolean;
  organizationAutoInvoiceSplitEnabled?: boolean;
  organizationAutoInvoiceSplitOffsetDays?: number;
  /** #5088 U5: the team's location cannot take a registration right now. */
  registrationLocked?: boolean;
  /** Its name, when the validator had one. */
  registrationLockedLocationName?: string | null;
}

interface JoinCodeFieldProps {
  id?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  athleteId?: string;
  onValidationChange?: (info: JoinCodeInfo | null) => void;
}

export const JoinCodeField: React.FC<JoinCodeFieldProps> = ({
  id = 'join-code',
  label = 'Team Join Code',
  value,
  onChange,
  error: externalError,
  placeholder = 'e.g., AB-1234',
  hint,
  required = false,
  athleteId,
  onValidationChange
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [codeInfo, setCodeInfo] = useState<JoinCodeInfo | null>(null);
  const validationRequestRef = useRef(0);

  const formatJoinCode = (val: string) => {
    // Remove all non-alphanumeric and uppercase
    const cleaned = val.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    
    if (cleaned.length <= 2) {
      return cleaned;
    }
    
    // Format as XX-XXXX (standard join code format)
    return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 6)}`;
  };

  const validateCode = useCallback(async (codeToValidate: string) => {
    const requestId = ++validationRequestRef.current;
    if (!codeToValidate.trim() || codeToValidate.length < 4) {
      setCodeInfo(null);
      setValidationError(null);
      return;
    }

    setIsValidating(true);
    setValidationError(null);
    
    try {
      const params = new URLSearchParams({ code: codeToValidate.trim() });
      if (athleteId) params.set('athleteId', athleteId);
      const response = await fetch(`/api/teams/validate-join-code?${params.toString()}`);
      const data = await response.json();
      if (requestId !== validationRequestRef.current) return;

      if (response.ok && data.success) {
        const nextInfo = {
          teamId: data.teamId,
          organizationId: data.organizationId,
          locationId: data.locationId || null,
          teamName: data.teamName,
          organizationName: data.organizationName,
          locationName: data.locationName,
          enrollmentFee: data.enrollmentFee,
          registrationFeeWaivedForCrossover: data.registrationFeeWaivedForCrossover === true,
          requiresSeasonAutoPay: data.requiresSeasonAutoPay === true,
          feeSchedule: Array.isArray(data.feeSchedule) ? data.feeSchedule : null,
          seasonStartDate: data.seasonStartDate || null,
          seasonEndDate: data.seasonEndDate || null,
          // U5a: optional toggles default to false so a gym with no split
          // configured (or migration drift) never accidentally shows the
          // "two payments per month" banner.
          organizationAutoInvoiceEnabled: data.organizationAutoInvoiceEnabled === true,
          organizationAutoInvoiceSplitEnabled: data.organizationAutoInvoiceSplitEnabled === true,
          organizationAutoInvoiceSplitOffsetDays:
            typeof data.organizationAutoInvoiceSplitOffsetDays === 'number'
              ? data.organizationAutoInvoiceSplitOffsetDays
              : 14,
          registrationLocked: data.registration_locked === true,
          registrationLockedLocationName: Array.isArray(data.registration_locked_locations)
            ? data.registration_locked_locations[0]?.name || null
            : null,
        };
        setCodeInfo(nextInfo);
        onValidationChange?.(nextInfo);
        setValidationError(null);
      } else {
        setCodeInfo(null);
        onValidationChange?.(null);
        // Don't show "Invalid" if it's too short, user might still be typing
        if (codeToValidate.length >= 7) {
          // Reason-coded denials (U3) map to shared copy; unknown/absent codes
          // fall back to the server error string verbatim.
          setValidationError(
            resolveJoinDenialMessage(data.reasonCode) || data.error || 'Invalid join code'
          );
        }
      }
    } catch (err) {
      if (requestId !== validationRequestRef.current) return;
      logger.error('Error validating join code:', err);
      setValidationError('Failed to validate code. Please try again.');
      setCodeInfo(null);
      onValidationChange?.(null);
    } finally {
      if (requestId === validationRequestRef.current) setIsValidating(false);
    }
  }, [athleteId, onValidationChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const formatted = formatJoinCode(rawValue);
    
    onChange(formatted);
    validationRequestRef.current += 1;
    setIsValidating(false);
    
    // Clear validation info when user types
    if (codeInfo) setCodeInfo(null);
    onValidationChange?.(null);
    if (validationError) setValidationError(null);

    // Auto-validate if it looks complete (XX-XXXX)
    if (formatted.length === 7) {
      validateCode(formatted);
    }
  };

  const handleBlur = () => {
    if (value) {
      validateCode(value);
    } else {
      setCodeInfo(null);
      setValidationError(null);
      onValidationChange?.(null);
    }
  };

  const currentError = externalError || validationError;

  return (
    <div className="space-y-2">
      <TextField
        id={id}
        name={id}
        label={label}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        error={currentError}
        hint={hint}
        required={required}
        suffix={isValidating ? <div className="animate-spin h-4 w-4 border-2 border-brand-500 border-t-transparent rounded-full" /> : <GoogleMaterialIcon name="key" size="xs" />}
      />

      {codeInfo && !currentError && (
        <div className="mt-2 flex items-start gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
            <GoogleMaterialIcon name="check" size="xs" className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <Typography variant="body-sm" className="font-bold text-green-900 dark:text-green-100">
              {codeInfo.teamName} — {codeInfo.organizationName}{codeInfo.locationName ? ` (${codeInfo.locationName})` : ''}
            </Typography>
            {codeInfo.enrollmentFee !== undefined && codeInfo.enrollmentFee !== null && (
              <Typography variant="body-xs" className="text-green-600 dark:text-green-400 font-medium mt-0.5">
                Team Registration Fee: ${codeInfo.enrollmentFee.toFixed(2)}
                {codeInfo.registrationFeeWaivedForCrossover ? ' (waived — crossover)' : ''}
              </Typography>
            )}
            {codeInfo.registrationFeeWaivedForCrossover && (
              <Typography variant="body-xs" className="text-green-700 dark:text-green-300 mt-1">
                This athlete is joining as a Crossover, so the one-time registration fee is waived — it was already paid for their primary team this season. Tuition and other team fees still apply.
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
